import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LaunchProgress = ({ isVisible, onClose, agentType, environment }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [logs, setLogs] = useState([]);

  const steps = [
    {
      id: 'initializing',
      title: 'Initializing Agent',
      description: 'Setting up agent configuration and parameters',
      icon: 'Settings',
      duration: 2000
    },
    {
      id: 'connecting',
      title: 'Establishing Connections',
      description: 'Connecting to external services and data sources',
      icon: 'Link',
      duration: 3000
    },
    {
      id: 'provisioning',
      title: 'Provisioning Environment',
      description: `Setting up ${environment} environment resources`,
      icon: 'Server',
      duration: 4000
    },
    {
      id: 'deploying',
      title: 'Deploying Agent',
      description: 'Launching AI agent and starting data collection',
      icon: 'Rocket',
      duration: 3000
    },
    {
      id: 'validating',
      title: 'Validating Deployment',
      description: 'Running health checks and validation tests',
      icon: 'CheckCircle',
      duration: 2000
    }
  ];

  useEffect(() => {
    if (!isVisible) {
      setCurrentStep(0);
      setIsComplete(false);
      setLogs([]);
      return;
    }

    const processSteps = async () => {
      for (let i = 0; i < steps?.length; i++) {
        setCurrentStep(i);
        
        // Add initial log
        setLogs(prev => [...prev, {
          id: Date.now() + i,
          timestamp: new Date()?.toLocaleTimeString(),
          message: `Starting: ${steps?.[i]?.title}`,
          type: 'info'
        }]);

        // Simulate step processing
        await new Promise(resolve => setTimeout(resolve, steps[i].duration));
        
        // Add completion log
        setLogs(prev => [...prev, {
          id: Date.now() + i + 1000,
          timestamp: new Date()?.toLocaleTimeString(),
          message: `Completed: ${steps?.[i]?.title}`,
          type: 'success'
        }]);
      }
      
      setIsComplete(true);
      setLogs(prev => [...prev, {
        id: Date.now() + 9999,
        timestamp: new Date()?.toLocaleTimeString(),
        message: `Agent "${agentType}" successfully deployed to ${environment}`,
        type: 'success'
      }]);
    };

    processSteps();
  }, [isVisible, agentType, environment]);

  if (!isVisible) return null;

  const NetworkAnimation = () => (
    <div className="relative w-32 h-32 mx-auto mb-8">
      {/* Central node */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full animate-pulse-soft" />
      
      {/* Orbiting nodes */}
      {[0, 1, 2, 3, 4, 5]?.map((index) => (
        <div
          key={index}
          className="absolute w-3 h-3 bg-accent rounded-full"
          style={{
            top: '50%',
            left: '50%',
            transform: `translate(-50%, -50%) rotate(${index * 60}deg) translateY(-48px)`,
            animation: `spin ${3 + index * 0.5}s linear infinite`
          }}
        />
      ))}
      
      {/* Connection lines */}
      <div className="absolute inset-0 opacity-30">
        <svg className="w-full h-full animate-spin" style={{ animationDuration: '20s' }}>
          <circle
            cx="50%"
            cy="50%"
            r="48"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            strokeDasharray="4 4"
            className="text-primary"
          />
        </svg>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-300 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-8">
          {!isComplete ? (
            <>
              <div className="text-center mb-8">
                <NetworkAnimation />
                <h2 className="text-2xl font-semibold text-card-foreground mb-2">
                  Launching Agent
                </h2>
                <p className="text-text-secondary">
                  Deploying {agentType} to {environment} environment...
                </p>
              </div>

              {/* Progress Steps */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  {steps?.map((step, index) => (
                    <div key={step?.id} className="flex flex-col items-center flex-1">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all duration-500 ${
                        index < currentStep 
                          ? 'bg-success text-white' 
                          : index === currentStep 
                            ? 'bg-primary text-white animate-pulse-soft' :'bg-muted text-muted-foreground'
                      }`}>
                        <Icon 
                          name={index < currentStep ? 'Check' : step?.icon} 
                          size={16} 
                        />
                      </div>
                      <div className="text-center">
                        <p className={`text-xs font-medium ${
                          index <= currentStep ? 'text-card-foreground' : 'text-muted-foreground'
                        }`}>
                          {step?.title}
                        </p>
                      </div>
                      {index < steps?.length - 1 && (
                        <div className={`hidden sm:block absolute h-0.5 w-16 mt-5 ml-16 transition-all duration-500 ${
                          index < currentStep ? 'bg-success' : 'bg-muted'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Current step description */}
                <div className="text-center">
                  <p className="text-sm text-text-secondary">
                    {steps?.[currentStep]?.description}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-success to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse-soft">
                <Icon name="CheckCircle" size={32} color="white" />
              </div>
              <h2 className="text-2xl font-semibold text-card-foreground mb-2">
                Agent Deployed Successfully!
              </h2>
              <p className="text-text-secondary">
                Your {agentType} is now running in the {environment} environment
              </p>
            </div>
          )}

          {/* Logs Section */}
          <div className="bg-muted rounded-lg p-4 max-h-64 overflow-y-auto">
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="Terminal" size={16} className="text-muted-foreground" />
              <h3 className="text-sm font-medium text-card-foreground">
                Deployment Logs
              </h3>
            </div>
            
            <div className="space-y-1 font-mono text-xs">
              {logs?.map((log) => (
                <div key={log?.id} className="flex items-start space-x-2">
                  <span className="text-muted-foreground flex-shrink-0">
                    [{log?.timestamp}]
                  </span>
                  <span className={`${
                    log?.type === 'success' ? 'text-success' : 
                    log?.type === 'error'? 'text-destructive' : 'text-card-foreground'
                  }`}>
                    {log?.message}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center space-x-4 mt-8">
            {isComplete ? (
              <>
                <Button
                  variant="outline"
                  onClick={onClose}
                  iconName="ArrowLeft"
                  iconPosition="left"
                >
                  Back to Launch
                </Button>
                <Button
                  variant="default"
                  onClick={() => window.location.href = '/dashboard'}
                  iconName="BarChart3"
                  iconPosition="right"
                >
                  View Dashboard
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                onClick={onClose}
                iconName="X"
                iconPosition="left"
              >
                Cancel Deployment
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LaunchProgress;